import { useState } from "react";
import { Navigate } from "react-router-dom";
import "./PlansPage.css";
import { useAllPlans } from "../../../hooks/useAllPlans";
import { useAuth } from "../../../hooks/useAuth";
import {
  createPlanAsync,
  deletePlanAsync,
  updatePlanAsync,
} from "../../../api/PlanApi";
import { Constants } from "../../../constants/constants";
import { CreatePlanRequest } from "../../../models/PlanModels";
import { Plan } from "../../../models/SubscriptionModels";
import PlanForm from "./PlanForm";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Toast from "../../../components/Toast";

type View =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; plan: Plan };

const PlansPage = () => {
  const { user } = useAuth();
  const { plans, loading, error, reload } = useAllPlans();
  const [view, setView] = useState<View>({ type: "list" });
  const [deleting, setDeleting] = useState<Plan | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  if (!user?.isPlatformOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const showToast = (
    message: string,
    type: "success" | "error" | "info",
  ) => setToast({ message, type });

  const handleSave = async (request: CreatePlanRequest, planId?: string) => {
    setSubmitting(true);
    try {
      if (planId) {
        const res = await updatePlanAsync(token, planId, request);
        if (res.success) {
          showToast(res.message || "Plan updated successfully!", "success");
          await reload();
          setView({ type: "list" });
        } else {
          showToast(res.message || "Failed to update plan", "error");
        }
      } else {
        const res = await createPlanAsync(token, request);
        if (res.success && res.result) {
          showToast(res.message || "Plan created successfully!", "success");
          await reload();
          setView({ type: "list" });
        } else {
          showToast(res.message || "Failed to create plan", "error");
        }
      }
    } catch (err) {
      console.error(err);
      showToast(
        planId
          ? "Failed to update plan. Please try again."
          : "Failed to create plan. Please try again.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setSubmitting(true);
    try {
      const res = await deletePlanAsync(token, deleting.id);
      if (res.success) {
        showToast(res.message || "Plan deleted successfully!", "success");
        await reload();
      } else {
        showToast(res.message || "Failed to delete plan", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to delete plan. Please try again.", "error");
    } finally {
      setSubmitting(false);
      setDeleting(null);
    }
  };

  const renderList = () => {
    if (loading) {
      return <div className="plans-empty">Loading plans…</div>;
    }

    if (error) {
      return (
        <div className="plans-empty">
          <p>{error}</p>
          <button className="plans-btn plans-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (plans.length === 0) {
      return (
        <div className="plans-empty">
          No plans yet. Click "New Plan" to create one.
        </div>
      );
    }

    return (
      <div className="plans-table-wrap">
        <table className="plans-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Price</th>
              <th>Currency</th>
              <th>Billing Period</th>
              <th>Active</th>
              <th>Public</th>
              <th>Sort</th>
              <th>Features</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan.id}>
                <td>
                  <span className="plans-name">{plan.name}</span>
                  {plan.description && (
                    <span className="plans-desc">{plan.description}</span>
                  )}
                </td>
                <td>
                  <span className="plans-code">{plan.code}</span>
                </td>
                <td>{formatPrice(plan.priceMonthly)}</td>
                <td>{plan.currency}</td>
                <td>{plan.billingPeriodDays} days</td>
                <td>
                  <span
                    className={`plans-badge${plan.isActive ? " plans-badge--on" : " plans-badge--off"}`}
                  >
                    {plan.isActive ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  <span
                    className={`plans-badge${plan.isPublic ? " plans-badge--on" : " plans-badge--off"}`}
                  >
                    {plan.isPublic ? "Yes" : "No"}
                  </span>
                </td>
                <td>{plan.sortOrder}</td>
                <td>{(plan.features ?? []).length}</td>
                <td className="plans-actions">
                  <button
                    className="plans-btn plans-btn--small"
                    onClick={() => setView({ type: "edit", plan })}
                  >
                    Edit
                  </button>
                  <button
                    className="plans-btn plans-btn--small plans-btn--danger"
                    onClick={() => setDeleting(plan)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="plans-page">
      {view.type === "list" ? (
        <>
          <div className="plans-header">
            <div>
              <h2>Plans</h2>
              <p>Create and manage the subscription plans offered to organizations.</p>
            </div>
            <button
              className="plans-btn plans-btn--primary"
              onClick={() => setView({ type: "create" })}
            >
              + New Plan
            </button>
          </div>
          {renderList()}
        </>
      ) : (
        <>
          <div className="plans-header">
            <div>
              <h2>{view.type === "edit" ? `Edit — ${view.plan.name}` : "New Plan"}</h2>
              <p>
                {view.type === "edit"
                  ? "Saving will replace the plan's feature list."
                  : "Define a new plan, its price and its feature limits."}
              </p>
            </div>
            <button
              className="plans-btn plans-btn--secondary"
              onClick={() => setView({ type: "list" })}
            >
              ← Back to plans
            </button>
          </div>
          <div className="plans-form-card">
            <PlanForm
              key={view.type === "edit" ? view.plan.id : "new"}
              initialPlan={view.type === "edit" ? view.plan : null}
              submitting={submitting}
              onCancel={() => setView({ type: "list" })}
              onSubmit={handleSave}
            />
          </div>
        </>
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete plan"
          message={`Are you sure you want to delete "${deleting.name}"? Existing subscriptions on this plan are not changed, but the plan will no longer be available.`}
          confirmLabel="Delete"
          busy={submitting}
          onCancel={() => setDeleting(null)}
          onConfirm={handleDelete}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

const formatPrice = (price: number) =>
  Number.isInteger(price) ? String(price) : price.toFixed(2);

export default PlansPage;